/* assets/vc-look-builder.js — <vc-look-builder>: "Build your look" on a colourway chapter (task-12-brief.md; spec D9 B,
 * §8.2). The markup, the data and the CSS are sections/vc-look-builder.liquid; the rules are assets/vc-logic.js.
 *
 * Ported from prototypes/configurator.html:309-561:
 *   - the rows (:358-387): an arch window with its roman numeral, the name, the hint, the dotted leader, the price;
 *   - render() (:448-515): the rows' state, the niche's light, the count, the status (swapped with its animation only
 *     when its content changes), the live line, the CTA; the tweened total (:430-440); the suggestion's add buttons, which
 *     keep keyboard focus in place (:529-536); the hover light (:542-549); "In your bag" (:517-520).
 * Changed (brief; controller rulings 1-7):
 *   - PIECES / SETS are the section's JSON through lookParts(): the carte's pieces and the complete sets it can compose.
 *     evaluate() is matchSet / nearestSet / lookTotal (lookStatus). The prototype's "either … or …" offer between two
 *     equal sets is not ported: nearestSet keeps the first of equals, and a colourway has one set today.
 *   - Rows are buttons with aria-pressed (ruling 7), numbered with roman() in the carte's order.
 *   - An exact match of a complete set turns the CTA into the link "View the set — £X" (data-mood: the colourway's),
 *     and the fitting gives way to a note: sizes are chosen on the set's page (the brief). Otherwise one Size and one
 *     Print select serve every chosen piece (fitValues: Custom measurements is chosen on each product page), and
 *     "Add N pieces to bag" adds each piece's variant (variantFor, by option name) at quantity 1 through
 *     window.vcCart.add (assets/vc-cart.js), which opens the bag. A piece with no available variant there disables the
 *     CTA and is named (fitCheck / fitMessage). A rejected add is said inline in role="status"; nothing here assumes
 *     the bag opened (ruling 5).
 *   - Every price goes through formatMoney with the shop's money format (ruling 3); moneyParts sets the currency and the
 *     pence small beside the Bodoni figure.
 *   - Task 14c, the multibuy rule (tools/multibuy.py; the theme settings group "VIPERCAT · Three pieces" printed as
 *     `data-vc-look-multibuy`): each piece carries `eligible` (its tag). When the chosen eligible pieces reach the minimum and no
 *     set matches, the total shows the full sum struck, the discounted total (multibuy(): a preview, the bag's is Shopify's) and the
 *     label; one piece short, the set-completion suggestion stays where it applies (it names the missing piece), else the nudge.
 *   - The niche lights by region on the k-full01 art (the regions JSON), uniformly elsewhere (--vcl-lit; ruling 6).
 *   - The entrance waits until the section comes into view (spec §8: IntersectionObserver-gated animation); reduced
 *     motion shows it at once and never tweens.
 *   - connectedCallback runs again when the theme editor re-renders the section (a new element):
 *     https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements
 */
// '@vc/logic': the import map's name (snippets/scripts.liquid) for the VERSIONED assets/vc-logic.js. A relative
// './vc-logic.js' drops this module's ?v= and loads the unversioned URL, which a returning visitor's browser keeps stale
// after an update (guard: tests/js/import-map.test.mjs).
import {
  AVAILABILITY_LABEL, fitCheck, fitMessage, fitValues, formatMoney, lookParts, lookStatus, moneyParts, multibuy, roman, shortTitle,
} from '@vc/logic';

const TWEEN_MS = 1000;            // configurator.html:432: the total counts to its new value
const ROW_DELAY = [260, 80];      // configurator.html:363: the rows are set one after another
const MSG_ADD = 'These pieces could not be added to your bag. Please try again.';
const NOTE_CUSTOM = 'Custom measurements · on each piece’s page'; // two lines, split at " · "
// the prototype's seal (configurator.html:443-446): a double ring and one continuous cat-mask contour, 24px
const SEAL = '<svg class="vc-look__seal" viewBox="-12 -12 24 24" aria-hidden="true" focusable="false"><g fill="none" stroke="currentColor" opacity=".9">'
  + '<circle r="11" stroke-width=".5"/><circle r="9.6" stroke-width=".5"/>'
  + '<path stroke-width=".6" stroke-linejoin="round" d="M-4.7 -.6 L-4.2 -5.6 L-1.6 -3.3 Q0 -3.8 1.6 -3.3 L4.2 -5.6 L4.7 -.6 C4.7 2.9 2.5 4.9 0 4.9 C-2.5 4.9 -4.7 2.9 -4.7 -.6 Z"/>'
  + '</g></svg>';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const andList = (a) => (a.length < 2 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);
const pieces = (n) => `${n} ${n === 1 ? 'piece' : 'pieces'}`;

/** A formatted price as markup: the currency and the pence set small (configurator.html:150, 180). */
function priceHtml(text) {
  const p = moneyParts(text);
  return (p.pre ? `<i class="vc-look__cur">${esc(p.pre)}</i>` : '') + esc(p.num)
    + (p.dec ? `<i class="vc-look__dec">${esc(p.dec)}</i>` : '') + (p.post ? `<i class="vc-look__cur">${esc(p.post)}</i>` : '');
}

function readJson(el, fallback) {
  if (!el) return fallback;
  try { return JSON.parse(el.textContent); } catch (err) { console.warn('[vc-look-builder]', err); return fallback; }
}

export class VcLookBuilder extends HTMLElement {
  connectedCallback() {
    if (this.built) { this.wire(); this.reveal(); return; } // moved in the page: disconnectedCallback let go of both
    const { pieces: carte, sets } = lookParts(readJson(this.querySelector('script[data-vc-look]'), []));
    if (carte.length < 2) return; // a look is two pieces or more; the section stays hidden (vc-look-builder.liquid)
    // any markup in the format is dropped, as Atelier reads it through a <template>'s text (snippets/price-filter.liquid:37)
    const fmt = readJson(this.querySelector('script[data-vc-look-money]'), '');
    this.format = typeof fmt === 'string' ? fmt.replace(/<[^>]*>/g, '') : '';
    this.pieces = carte.map((p) => ({ ...p, name: shortTitle(p.title, this.dataset.cwName) }));
    this.sets = sets;
    const mb = readJson(this.querySelector('script[data-vc-look-multibuy]'), null);
    this.mb = mb && typeof mb === 'object'
      ? { min: Number(mb.min) >= 1 ? Number(mb.min) : 3, percent: Number(mb.percent) > 0 ? Number(mb.percent) : 10,
          label: typeof mb.label === 'string' ? mb.label : '', nudge: typeof mb.nudge === 'string' ? mb.nudge : '' }
      : null;
    this.sel = new Set();
    this.size = '';
    this.print = '';
    this.shown = 0;
    this.lastKey = '';
    this.statusSay = null; // null until the first render: the live region says nothing on load
    this.q = (s) => this.querySelector(s);
    this.els = {
      list: this.q('[data-vc-look-list]'), status: this.q('[data-vc-look-status]'), was: this.q('[data-vc-look-was]'),
      now: this.q('[data-vc-look-now]'), size: this.q('[data-vc-look-size]'), print: this.q('[data-vc-look-print]'),
      sizeField: this.q('[data-vc-look-field="size"]'), printField: this.q('[data-vc-look-field="print"]'),
      fit: this.q('[data-vc-look-fit]'), fitnote: this.q('[data-vc-look-fitnote]'), add: this.q('[data-vc-look-add]'),
      set: this.q('[data-vc-look-set]'), note: this.q('[data-vc-look-note]'), msg: this.q('[data-vc-look-msg]'),
      live: this.q('[data-vc-look-live]'), count: this.q('[data-vc-look-count]'), halo: this.q('[data-vc-look-halo]'),
      floorglow: this.q('[data-vc-look-floorglow]'), lit: this.q('[data-vc-look-lit]'), garment: this.q('[data-vc-look-garment]'),
    };
    if (!this.els.list || !this.els.add || !this.els.status) return;
    this.rows = new Map();
    this.els.list.replaceChildren(...this.pieces.map((p, i) => this.row(p, i)));
    this.regions = this.buildRegions();
    this.wire();
    this.built = true;
    this.render();
    this.classList.add('is-built');
    this.reveal();
  }

  disconnectedCallback() {
    if (this.ac) this.ac.abort();
    if (this.io) this.io.disconnect();
    cancelAnimationFrame(this.raf);
  }

  money(cents) { return formatMoney(cents, this.format); }

  /* ---------- building ---------- */
  row(p, i) {
    const li = document.createElement('li');
    li.className = 'vc-look__item vc-look__in';
    li.style.transitionDelay = `${ROW_DELAY[0] + i * ROW_DELAY[1]}ms`;
    li.innerHTML = `<button type="button" class="vc-look__row" aria-pressed="false" data-key="${esc(p.key)}">`
      + '<span class="vc-look__mark" aria-hidden="true"><svg viewBox="0 0 18 24" focusable="false">'
      + '<path class="vc-look__m-fill" d="M3 21.5 V9.5 A6 6 0 0 1 15 9.5 V21.5 Z"/>'
      + '<path class="vc-look__m-inner" d="M3 21.5 V9.5 A6 6 0 0 1 15 9.5 V21.5 Z"/>'
      + '<path class="vc-look__m-line" d="M.5 24 V9 A8.5 8.5 0 0 1 17.5 9 V24 Z"/>'
      + `<text class="vc-look__m-num" x="9" y="18.4" text-anchor="middle">${roman(i + 1)}</text></svg></span>`
      + `<span class="vc-look__namewrap"><span class="vc-look__name">${esc(p.name)}</span>`
      + '<span class="vc-look__hint" aria-hidden="true">completes the set</span><span class="vc-look__leader" aria-hidden="true"></span>'
      + `<span class="vc-look__price">${priceHtml(this.money(p.price))}</span></span></button>`;
    this.rows.set(p.key, li.firstChild);
    return li;
  }

  /** The k-full01 art only: one light layer per piece that has a region (configurator.html:378-386). */
  buildRegions() {
    const out = new Map();
    const map = readJson(this.q('script[data-vc-look-regions]'), null);
    if (!map || !this.els.garment) return out;
    for (const p of this.pieces) {
      const r = map[p.key];
      if (!r || typeof r.src !== 'string' || !Array.isArray(r.rect) || r.rect.length !== 4) continue;
      const [l, t, w, h] = r.rect.map(Number);
      const pos = `left:${l}%;top:${t}%;width:${w}%;height:${h}%`;
      const g = document.createElement('div');
      g.className = 'vc-look__region';
      g.innerHTML = `<img alt="" src="${esc(r.src)}" style="${pos}" loading="lazy" decoding="async">`
        + `<div class="vc-look__warm"><img alt="" src="${esc(r.src)}" style="${pos}" loading="lazy" decoding="async"></div>`;
      this.els.garment.append(g);
      out.set(p.key, g);
    }
    return out;
  }

  wire() {
    this.ac = new AbortController();
    const on = (el, type, fn) => el && el.addEventListener(type, fn, { signal: this.ac.signal });
    on(this.els.list, 'click', (e) => {
      const b = e.target.closest('.vc-look__row');
      if (!b) return;
      const k = b.dataset.key;
      if (this.sel.has(k)) this.sel.delete(k); else this.sel.add(k);
      this.render();
    });
    on(this.els.status, 'click', (e) => {
      const b = e.target.closest('[data-add]');
      if (!b) return;
      const k = b.dataset.add;
      this.sel.add(k);
      this.render();
      // the status was rebuilt: keep keyboard focus in place (configurator.html:533-535)
      const next = this.els.status.querySelector('[data-add]');
      (next || this.rows.get(k)).focus({ preventScroll: true });
    });
    on(this.els.size, 'change', () => { this.size = this.els.size.value; this.render(); });
    on(this.els.print, 'change', () => { this.print = this.els.print.value; this.render(); });
    on(this.els.add, 'click', () => this.onAdd());
    // a touch is a choice, not a hover: it would leave a piece half-lit (touch is left out as the cards do,
    // assets/vc-collection.js:87-102)
    const hover = (e, onOff) => {
      if (e.pointerType === 'touch') return;
      const b = e.target.closest('.vc-look__row');
      if (!b || (!onOff && b.contains(e.relatedTarget))) return;
      this.hoverKey = onOff ? b.dataset.key : null;
      this.light();
    };
    on(this.els.list, 'pointerover', (e) => hover(e, true));
    on(this.els.list, 'pointerout', (e) => hover(e, false));
    on(this.els.list, 'focusin', (e) => hover(e, true));
    on(this.els.list, 'focusout', (e) => hover(e, false));
    // "In your bag" holds only while its pieces are in the bag (assets/vc-cart.js: vc:cart:changed {count, cart})
    on(document, 'vc:cart:changed', (e) => {
      const ids = new Set(((e.detail && e.detail.cart && e.detail.cart.items) || []).map((it) => it.variant_id));
      if (this.added && !this.added.ids.every((id) => ids.has(id))) { this.added = null; this.render(); }
    });
  }

  reveal() {
    if (reducedMotion() || !('IntersectionObserver' in window)) { this.classList.add('is-ready'); return; }
    this.io = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      this.classList.add('is-ready');
      this.io.disconnect();
    }, { rootMargin: '0px 0px -10% 0px' });
    this.io.observe(this);
  }

  /* ---------- state ---------- */
  selected() { return this.pieces.filter((p) => this.sel.has(p.key)); }
  signature() { return `${[...this.sel].sort().join(',')}|${this.size}|${this.print}`; }

  /** What the CTA can do now: add these lines, or why it waits (and what to say about it). The note beside it gives the
   *  look's availability once it is resolved; until then, where Custom measurements is, since the Size select leaves it
   *  out (the brief: "it is available on each product page"). */
  fit(chosen, matched) {
    const src = chosen.length ? chosen : this.pieces; // nothing chosen: the carte's values, the selects disabled
    const values = fitValues(src);
    if (!values.sizes.includes(this.size)) this.size = '';
    if (!values.prints.includes(this.print)) this.print = values.prints[0] || '';
    const hint = values.custom ? NOTE_CUSTOM : '';
    const out = { values, lines: null, label: 'Add to bag', note: hint, msg: '', wait: 'none' };
    if (matched) return out;
    if (!chosen.length) { out.wait = 'pieces'; return out; }
    out.label = `Add ${pieces(chosen.length)} to bag`;
    if (values.sizes.length && !this.size) { out.label = 'Select a size'; out.wait = 'size'; return out; }
    const check = fitCheck(chosen, this.size, this.print);
    if (!check.ok) {
      const combo = [this.size, values.prints.length ? this.print : ''].filter(Boolean).join(' · ');
      out.msg = fitMessage(check.soldOut.map((p) => p.name), check.missing.map((p) => p.name), combo);
      out.wait = 'fit';
      return out;
    }
    out.lines = check.lines;
    out.note = AVAILABILITY_LABEL[check.state] || '';
    return out;
  }

  light() {
    const n = this.sel.size, total = this.pieces.length;
    for (const [k, g] of this.regions) {
      g.classList.toggle('is-on', this.sel.has(k));
      g.classList.toggle('is-hover', this.hoverKey === k && !this.sel.has(k));
    }
    if (this.els.lit) { // the uniform light: the share of the pieces chosen, a hovered one previewed
      const hover = this.hoverKey && !this.sel.has(this.hoverKey) ? 0.6 : 0;
      this.els.lit.style.setProperty('--vcl-lit', ((n + hover) / total).toFixed(3));
    }
    if (this.els.halo) this.els.halo.style.opacity = (0.08 + 0.92 * n / total).toFixed(3);            // configurator.html:467
    if (this.els.floorglow) this.els.floorglow.style.opacity = (0.15 + 0.85 * n / total).toFixed(3);  // :468
  }

  /** The multibuy line under the status (Task 14c): the label once the discount applies (enough eligible pieces, no set), else, one
   *  piece short and no set to complete (the suggestion names that piece and stays), the nudge. `mb` is multibuy()'s answer. */
  mbLine(r, mb) {
    if (!mb || r.set) return { key: '', text: '' };
    if (mb.qualifies && this.mb.label) return { key: 'l', text: this.mb.label };
    if (mb.needed === 1 && !r.near && this.mb.nudge) return { key: 'n', text: this.mb.nudge };
    return { key: '', text: '' };
  }

  status(r, n, mb) {
    const m = r.set;
    if (m) {
      const save = r.saving > 0;
      return { key: `m:${m.key}`,
        html: `<div class="vc-look__slabel">${SEAL}<span>The set</span></div><div class="vc-look__setname">${esc(m.title)}</div>`
          + `<div class="vc-look__sline">Composed as a set${save ? `, a saving of <b>${priceHtml(this.money(r.saving))}</b>` : ''}</div>`,
        say: `${m.title}, composed as a set: ${this.money(r.price)}${save ? `, a saving of ${this.money(r.saving)}` : ''}.` };
    }
    if (!n) {
      return { key: 'e',
        html: '<div class="vc-look__slabel"><span>The carte</span></div><div class="vc-look__setname">Choose your pieces</div>'
          + (this.sets.length ? '<div class="vc-look__sline">Complete a set and it is priced as one.</div>' : ''),
        say: 'No pieces chosen.' };
    }
    const line = this.mbLine(r, mb);
    const mbHtml = line.text ? `<div class="vc-look__sline vc-look__sline--mb">${esc(line.text)}</div>` : '';
    const mbSay = !line.text ? '' : line.key === 'l' ? ` ${line.text}: ${this.money(mb.total)}.` : ` ${line.text}.`;
    if (r.near) {
      const miss = r.near.missing.map((k) => this.pieces.find((p) => p.key === k)).filter(Boolean);
      const links = andList(miss.map((p) => `<button type="button" class="vc-look__add" data-add="${esc(p.key)}">the ${esc(p.name)}</button>`));
      const save = r.nearSaving > 0;
      const set = r.near.set;
      return { key: `s:${set.key}:${r.near.missing.join(',')}:${line.key}`,
        html: '<div class="vc-look__slabel"><span>Complete the set</span></div>'
          + `<div class="vc-look__suggest">Add ${links} to complete <span class="vc-look__s-set">${esc(set.title)}</span>`
          + (save ? ` <span class="vc-look__s-save">— and save ${esc(this.money(r.nearSaving))}.</span>` : '.') + '</div>' + mbHtml,
        say: `${pieces(n)}, ${this.money(r.sum)}. Add ${andList(miss.map((p) => `the ${p.name}`))} to complete ${set.title}`
          + `${save ? ` and save ${this.money(r.nearSaving)}` : ''}.${mbSay}` };
    }
    return { key: `n:${line.key}`,
      html: `<div class="vc-look__slabel"><span>Your look</span></div><div class="vc-look__setname">A look of your own</div>${mbHtml}`,
      say: `${pieces(n)}, ${this.money(r.sum)}.${mbSay}` };
  }

  render() {
    const chosen = this.selected();
    const n = chosen.length;
    const r = lookStatus(chosen.map((p) => p.key), this.pieces, this.sets);
    // the multibuy rule on the chosen pieces (the eligible ones count); it prices the look only when no set does
    const mb = this.mb ? multibuy(chosen.map((p) => ({ price: p.price, eligible: !!p.eligible, quantity: 1 })), this.mb) : null;
    const off = !!mb && !r.set && mb.qualifies;
    const missing = r.near ? r.near.missing : [];
    for (const p of this.pieces) {
      const b = this.rows.get(p.key);
      b.setAttribute('aria-pressed', String(this.sel.has(p.key)));
      b.classList.toggle('is-suggested', !r.set && missing.includes(p.key) && missing.length <= 2); // configurator.html:459
    }
    this.classList.toggle('is-matched', !!r.set);
    this.classList.toggle('is-discount', off);
    this.classList.toggle('is-empty', n === 0);
    this.light();
    if (this.els.count) this.els.count.textContent = `${n ? roman(n) : 'None'} of ${roman(this.pieces.length)}`;

    const st = this.status(r, n, mb);
    if (st.key !== this.lastKey) {
      this.els.status.innerHTML = st.html;
      this.els.status.classList.remove('is-swap');
      void this.els.status.offsetWidth; // restart the swap animation (configurator.html:510)
      this.els.status.classList.add('is-swap');
      this.lastKey = st.key;
    }
    // the live line speaks when the status or the total changes (ruling 7), not on every render
    if (st.say !== this.statusSay) { if (this.statusSay !== null) this.els.live.textContent = st.say; this.statusSay = st.say; }
    this.els.was.innerHTML = r.set || off ? priceHtml(this.money(r.sum)) : '';
    this.tweenTo(off ? mb.total : r.price);
    this.renderFit(chosen, r.set);
  }

  renderFit(chosen, matched) {
    const f = this.fit(chosen, matched);
    const { size, print, sizeField, printField, fit, fitnote, add, set, note, msg } = this.els;
    this.fill(size, f.values.sizes, this.size, 'Select');
    this.fill(print, f.values.prints, this.print, null);
    size.disabled = !chosen.length || !!matched;
    print.disabled = !chosen.length || !!matched;
    sizeField.hidden = !f.values.sizes.length;
    printField.hidden = !f.values.prints.length;
    fit.classList.toggle('is-set', !!matched);
    fitnote.hidden = !matched;

    let noteText = f.note;
    add.hidden = !!matched;
    set.hidden = !matched;
    if (matched) {
      set.href = matched.url;
      set.dataset.mood = this.dataset.vcMood === 'sweet' ? 'sweet' : 'noir';
      set.querySelectorAll('[data-vc-look-set-label]').forEach((el) => { el.textContent = `View the set — ${this.money(matched.price)}`; });
      const v = Array.isArray(matched.variants) ? matched.variants[0] : null;
      noteText = (v && AVAILABILITY_LABEL[v.state]) || '';
    } else {
      set.removeAttribute('href');
    }
    const sig = this.signature();
    const added = !matched && this.added && this.added.sig === sig;
    const label = added ? 'In your bag' : f.label;
    add.querySelectorAll('[data-vc-look-label]').forEach((el) => { el.textContent = f.label; });
    add.setAttribute('aria-label', label);
    add.classList.toggle('is-added', !!added);
    add.setAttribute('aria-disabled', String(!added && (!f.lines || !!this.busy)));
    if (this.busy) add.setAttribute('aria-busy', 'true'); else add.removeAttribute('aria-busy');
    this.lines = f.lines;
    this.wait = f.wait;

    const parts = noteText ? noteText.split(' · ') : [];
    const noteHtml = parts.map((s) => `<span>${esc(s)}</span>`).join('');
    if (note.innerHTML !== noteHtml) note.innerHTML = noteHtml;
    const text = this.error && this.error.sig === sig ? this.error.text : f.msg;
    if (msg.textContent !== text) msg.textContent = text;
  }

  /** Rebuild a select's options only when its list changes, so an open or focused select is never disturbed. */
  fill(select, values, value, placeholder) {
    const want = (placeholder !== null ? [''] : []).concat(values);
    const have = [...select.options].map((o) => o.value);
    if (have.join('\u0000') !== want.join('\u0000')) {
      select.replaceChildren(...want.map((v) => new Option(v === '' ? placeholder : v, v)));
    }
    select.value = value;
  }

  tweenTo(v) {
    cancelAnimationFrame(this.raf);
    const from = this.shown;
    const paint = (x) => { this.shown = x; this.els.now.innerHTML = priceHtml(this.money(x)); };
    if (from === v || reducedMotion() || !this.classList.contains('is-ready')) { paint(v); return; }
    const t0 = performance.now();
    const step = (t) => {
      const k = Math.max(0, Math.min(1, (t - t0) / TWEEN_MS));
      const e = 1 - Math.pow(1 - k, 4); // configurator.html:435
      // whole units while it counts, so the pence do not flicker; the exact figure on the last frame
      paint(k < 1 ? Math.round((from + (v - from) * e) / 100) * 100 : v);
      if (k < 1) this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  /* ---------- the bag ---------- */
  async onAdd() {
    if (this.added && this.added.sig === this.signature()) { if (window.vcCart) window.vcCart.open(); return; }
    if (this.busy) return;
    if (!this.lines) {
      if (this.wait === 'size' && !this.els.size.disabled) this.els.size.focus();
      return;
    }
    const sig = this.signature();
    const items = this.lines.map((l) => ({ id: l.variant.id, quantity: 1 }));
    if (!window.vcCart || typeof window.vcCart.add !== 'function') {
      this.error = { sig, text: MSG_ADD };
      this.render();
      return;
    }
    this.error = null;
    this.busy = true;
    this.render();
    try {
      await window.vcCart.add(items);
      this.added = { sig, ids: items.map((it) => it.id) };
      this.els.live.textContent = `${pieces(items.length)} added to your bag.`;
    } catch (err) {
      // vcCart.add rejects with the reason to show (Shopify's description on a 422); the bag may be open with it too
      this.error = { sig, text: (err && err.message) || MSG_ADD };
    } finally {
      this.busy = false;
      this.render();
    }
  }
}

if (!customElements.get('vc-look-builder')) customElements.define('vc-look-builder', VcLookBuilder);
